import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useEffect, useMemo, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/patterns";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Loader2, Lock } from "lucide-react";
import type { PermissionDefinition, RoleDetail } from "@/types";

/** Group headings in the matrix, in the order people think about the work */
const MODULES: { key: string; label: string }[] = [
  { key: "lead", label: "Leads" },
  { key: "account", label: "Accounts" },
  { key: "contact", label: "Contacts" },
  { key: "deal", label: "Deals" },
  { key: "proposal", label: "Proposals" },
  { key: "agreement", label: "Agreements" },
  { key: "quote", label: "Quotes" },
  { key: "order", label: "Orders" },
  { key: "sample", label: "Samples" },
  { key: "price_list", label: "Price lists" },
  { key: "product", label: "Products" },
  { key: "activity", label: "Activities" },
  { key: "calendar", label: "Calendar" },
  { key: "email", label: "Email" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "call", label: "Calling" },
  { key: "target", label: "Targets" },
  { key: "report", label: "Reports" },
  { key: "user", label: "Administration" },
  { key: "role", label: "Administration" },
  { key: "setting", label: "Administration" },
  { key: "audit", label: "Administration" },
  { key: "ai_agent", label: "The AI assistant can…" },
];

/** Which records a role sees, one choice per kind of record */
const SCOPE_RESOURCES = [
  { key: "lead", label: "Leads" },
  { key: "deal", label: "Deals" },
  { key: "account", label: "Accounts" },
  { key: "contact", label: "Contacts" },
  { key: "quote", label: "Quotes" },
  { key: "report", label: "Reports" },
];
const SCOPES = [
  { key: "own", label: "Their own" },
  { key: "team", label: "Their team’s" },
  { key: "all", label: "Everyone’s" },
];

const ROLE_BLURB: Record<string, string> = {
  SALES_HEAD: "Leads a team, approves discounts, sets targets.",
  SALES_EXEC: "Works their own leads and deals day to day.",
};
const SCOPE_WORDS: Record<string, string> = { own: "only their own", team: "their team’s", all: "everyone’s" };

/** What can be edited for a role; compared with the saved copy to find unsaved changes */
type Draft = { permissions: string[]; data_scopes: Record<string, string>; discount_limit_pct: number };
const toDraft = (r: RoleDetail): Draft => ({
  permissions: [...(r.permissions || [])].sort(),
  data_scopes: { ...(r.data_scopes || {}) },
  discount_limit_pct: Number(r.discount_limit_pct || 0),
});
const same = (a: Draft, b: Draft) =>
  JSON.stringify([a.permissions, Object.entries(a.data_scopes).sort(), a.discount_limit_pct]) ===
  JSON.stringify([b.permissions, Object.entries(b.data_scopes).sort(), b.discount_limit_pct]);

type RoleRow = RoleDetail & { user_count?: number };

export function RolesPage() {
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [catalog, setCatalog] = useState<PermissionDefinition[]>([]);
  const [saved, setSaved] = useState<Record<string, Draft>>({});
  const [draft, setDraft] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { success, error } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const [rolesRes, catalogRes] = await Promise.all([api.get("/api/UserRole/List"), api.get("/api/UserRole/PermissionsCatalog")]);
      const list = unwrapArray<RoleRow>(rolesRes)
        .filter((r) => r.code?.toUpperCase() !== "SUPER_ADMIN")
        .sort((a, b) => (a.code === "SALES_HEAD" ? -1 : b.code === "SALES_HEAD" ? 1 : 0));
      const copy = Object.fromEntries(list.map((r) => [r.id, toDraft(r)]));
      setRoles(list);
      setCatalog(unwrapArray<PermissionDefinition>(catalogRes));
      setSaved(copy);
      setDraft(JSON.parse(JSON.stringify(copy)));
    } catch (err: any) {
      error("Could not load roles", err.message || "Please try again");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  /** Catalog entries under the matrix headings (Administration collects four modules) */
  const groups = useMemo(() => {
    const out: { label: string; items: PermissionDefinition[] }[] = [];
    for (const m of MODULES) {
      const items = catalog.filter((c) => c.module === m.key);
      if (!items.length) continue;
      const last = out[out.length - 1];
      if (last && last.label === m.label) last.items.push(...items);
      else out.push({ label: m.label, items });
    }
    const known = new Set(MODULES.map((m) => m.key));
    const other = catalog.filter((c) => !known.has(c.module));
    if (other.length) out.push({ label: "Other", items: other });
    return out;
  }, [catalog]);

  const dr = (id: string) => draft[id] as Draft;
  const sv = (id: string) => saved[id] as Draft;

  const changedRoles = roles.filter((r) => draft[r.id] && saved[r.id] && !same(dr(r.id), sv(r.id)));
  const changedCount = changedRoles.reduce((n, r) => {
    const a = new Set(dr(r.id).permissions);
    const b = new Set(sv(r.id).permissions);
    let diff = [...a].filter((k) => !b.has(k)).length + [...b].filter((k) => !a.has(k)).length;
    for (const sc of SCOPE_RESOURCES) if ((dr(r.id).data_scopes[sc.key] || "own") !== (sv(r.id).data_scopes[sc.key] || "own")) diff++;
    if (dr(r.id).discount_limit_pct !== sv(r.id).discount_limit_pct) diff++;
    return n + diff;
  }, 0);
  const dirty = changedRoles.length > 0;

  const patch = (roleId: string, change: (d: Draft) => Partial<Draft>) =>
    setDraft((d) => ({ ...d, [roleId]: { ...(d[roleId] as Draft), ...change(d[roleId] as Draft) } }));
  const togglePermission = (roleId: string, key: string) =>
    patch(roleId, (d) => ({ permissions: d.permissions.includes(key) ? d.permissions.filter((k) => k !== key) : [...d.permissions, key].sort() }));
  const setScope = (roleId: string, resource: string, scope: string) => patch(roleId, (d) => ({ data_scopes: { ...d.data_scopes, [resource]: scope } }));
  const setDiscount = (roleId: string, value: string) => patch(roleId, () => ({ discount_limit_pct: Math.min(100, Math.max(0, Number(value) || 0)) }));

  const discard = () => setDraft(JSON.parse(JSON.stringify(saved)));

  const save = async () => {
    setSaving(true);
    try {
      for (const r of changedRoles) {
        await api.put(`/api/UserRole/UpdatePermissions/${r.id}`, dr(r.id));
      }
      success("Roles saved", "Changes apply the next time each person loads a page");
      await load();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const cellClass = "border-b border-border/60 px-2.5 text-center";

  return (
    <div className="space-y-4">
      <PageHeader
        title="Roles & permissions"
        actions={
          <>
            {dirty && <span className="text-xs font-medium text-warning">{changedCount} unsaved change{changedCount === 1 ? "" : "s"}</span>}
            <Button variant="outline" size="sm" onClick={discard} disabled={!dirty || saving}>
              Discard
            </Button>
            <Button size="sm" onClick={save} disabled={!dirty || saving}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : "Save changes"}
            </Button>
          </>
        }
      />

      <p className="text-[13px] text-muted-foreground">Three fixed roles. Choose what each can do, and whose records they see.</p>

      {loading ? (
        <TableSkeleton rows={6} columns={4} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
            <div className="flex flex-col gap-1 rounded-[10px] border border-border bg-card px-3.5 py-3">
              <span className="font-semibold text-foreground">Super admin</span>
              <span className="text-xs text-muted-foreground">Runs the CRM: users, settings, every record.</span>
              <span className="mt-1 text-xs">
                Sees records: <strong>everyone’s</strong>
              </span>
            </div>
            {roles.map((r) => (
              <div key={r.id} className="flex flex-col gap-1 rounded-[10px] border border-border bg-card px-3.5 py-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">{r.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {r.user_count ?? 0} {r.user_count === 1 ? "person" : "people"}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{ROLE_BLURB[r.code?.toUpperCase()] || "Custom role."}</span>
                <span className="mt-1 text-xs">
                  Sees records: <strong>{SCOPE_WORDS[draft[r.id]?.data_scopes.deal || "own"]}</strong>
                </span>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-[10px] border border-border bg-card">
            <table className="w-full min-w-[760px] border-collapse text-[12.5px]">
              <thead>
                <tr className="bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="h-9 border-b border-border px-3.5 font-semibold">Permission</th>
                  <th className="w-[150px] border-b border-border px-2.5 text-center font-semibold">Super admin</th>
                  {roles.map((r) => (
                    <th key={r.id} className="w-[150px] border-b border-border px-2.5 text-center font-semibold">
                      {r.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.label}>
                    <tr>
                      <td colSpan={2 + roles.length} className="border-b border-border bg-muted/40 px-3.5 pb-1.5 pt-2.5 text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {g.label}
                      </td>
                    </tr>
                    {g.items.map((p) => (
                      <tr key={p.key}>
                        <td className="border-b border-border/60 px-3.5 py-2">
                          <span className="font-medium text-foreground">{p.description}</span>
                          <span className="block font-mono text-[11px] text-muted-foreground">{p.key}</span>
                        </td>
                        <td className={cellClass}>
                          <Always />
                        </td>
                        {roles.map((r) => {
                          const on = draft[r.id]?.permissions.includes(p.key);
                          const was = saved[r.id]?.permissions.includes(p.key);
                          return (
                            <td key={r.id} className={`${cellClass} ${on !== was ? "bg-warning-soft" : ""}`}>
                              <input
                                type="checkbox"
                                checked={!!on}
                                onChange={() => togglePermission(r.id, p.key)}
                                aria-label={`${p.description} for ${r.name}`}
                                className="size-4 accent-primary"
                              />
                              {on !== was && <span className="block text-[10.5px] font-semibold text-warning">changed</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}

                <tr>
                  <td colSpan={2 + roles.length} className="border-b border-border bg-muted/40 px-3.5 pb-1.5 pt-2.5 text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Whose records they see
                  </td>
                </tr>
                {SCOPE_RESOURCES.map((sc) => (
                  <tr key={sc.key}>
                    <td className="border-b border-border/60 px-3.5 py-2 font-medium text-foreground">{sc.label}</td>
                    <td className={cellClass}>
                      <Always label="Everyone’s" />
                    </td>
                    {roles.map((r) => {
                      const value = draft[r.id]?.data_scopes[sc.key] || "own";
                      const changed = value !== (saved[r.id]?.data_scopes[sc.key] || "own");
                      return (
                        <td key={r.id} className={`${cellClass} ${changed ? "bg-warning-soft" : ""}`}>
                          <SearchableSelect
                            value={value}
                            onChange={(e) => setScope(r.id, sc.key, e.target.value)}
                            aria-label={`${sc.label} visible to ${r.name}`}
                            className="h-7 rounded-md border border-input bg-card px-1.5 text-xs"
                          >
                            {SCOPES.map((o) => (
                              <option key={o.key} value={o.key}>
                                {o.label}
                              </option>
                            ))}
                          </SearchableSelect>
                        </td>
                      );
                    })}
                  </tr>
                ))}

                <tr>
                  <td colSpan={2 + roles.length} className="border-b border-border bg-muted/40 px-3.5 pb-1.5 pt-2.5 text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Discounts
                  </td>
                </tr>
                <tr>
                  <td className="px-3.5 py-2">
                    <span className="font-medium text-foreground">Largest discount they can give without approval</span>
                    <span className="block text-[11px] text-muted-foreground">Quotes above this go to someone with approval rights</span>
                  </td>
                  <td className={cellClass}>
                    <Always label="No limit" />
                  </td>
                  {roles.map((r) => {
                    const changed = draft[r.id]?.discount_limit_pct !== saved[r.id]?.discount_limit_pct;
                    return (
                      <td key={r.id} className={`${cellClass} border-b-0 ${changed ? "bg-warning-soft" : ""}`}>
                        <span className="inline-flex items-center gap-1">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={draft[r.id]?.discount_limit_pct ?? 0}
                            onChange={(e) => setDiscount(r.id, e.target.value)}
                            aria-label={`Discount limit for ${r.name}`}
                            className="h-7 w-16 rounded-md border border-input bg-card px-1.5 text-right font-mono text-xs"
                          />
                          %
                        </span>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-xs text-muted-foreground">Changes apply the next time each person loads a page. Every change is written to the security log.</p>
        </>
      )}
    </div>
  );
}

function Always({ label = "Always" }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground">
      <Lock className="size-3" />
      {label}
    </span>
  );
}
